import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '../components/common/Header';
import Snackbar from '../components/common/Snackbar';
import useSnackbar from '../hooks/useSnackbar';
import { getStoreSeats, updateStoreSeats } from '../services/adminSeatService';
import { getSeatPhotos, uploadSeatPhotos } from '../services/adminPhotoService';

const WORLD_SIZE = 6000;
const GRID = 40;
const MIN_ZOOM = 0.15;
const MAX_ZOOM = 3.0;

const SIZE_TABLE = {
  S: { width: 66, height: 112 },
  M: { width: 76, height: 133 },
  L: { width: 86, height: 150 },
  XL: { width: 96, height: 165 },
};

const seatTypeFromCapacity = (capacity) => {
  if (!capacity || capacity <= 1) return 'SINGLE';
  if (capacity === 2) return 'DOUBLE';
  return 'GROUP';
};

const newClientId = () => `local-${Math.random().toString(36).slice(2, 10)}`;

const mapSeatFromApi = (s) => ({
  clientId: s.id || newClientId(),
  id: s.id,
  kind: 'SEAT',
  seatNumber: s.seatNumber,
  seatType: s.seatType,
  basePrice: s.basePrice != null ? Number(s.basePrice) : null,
  positionX: s.positionX,
  positionY: s.positionY,
  maxOccupancy: s.maxOccupancy ?? 1,
  hasOutlet: !!s.hasOutlet,
  width: s.width != null ? Number(s.width) : SIZE_TABLE.M.width,
  height: s.height != null ? Number(s.height) : SIZE_TABLE.M.height,
  size: s.size || 'M',
  isShared: !!s.isShared,
  isInUse: !!s.isInUse,
});

const mapEntranceFromApi = (e) => ({
  clientId: e.id || newClientId(),
  id: e.id,
  kind: 'ENTRANCE',
  label: e.label || '입구',
  positionX: e.positionX,
  positionY: e.positionY,
});

const mapCounterFromApi = (c) => ({
  clientId: c.id || newClientId(),
  id: c.id,
  kind: 'COUNTER',
  label: c.label || '카운터',
  positionX: c.positionX,
  positionY: c.positionY,
  width: c.width != null ? Number(c.width) : SIZE_TABLE.M.width,
  height: c.height != null ? Number(c.height) : SIZE_TABLE.M.height,
  size: c.size || 'M',
});

const nextSeatNumber = (components) => {
  const used = new Set();
  components.filter((c) => c.kind === 'SEAT').forEach((s) => {
    const n = parseInt(s.seatNumber, 10);
    if (!Number.isNaN(n)) used.add(n);
  });
  let n = 1;
  while (used.has(n)) n += 1;
  return String(n);
};

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

const StoreSeatLayout = () => {
  const { storeId } = useParams();
  const navigate = useNavigate();
  const { snackbar, showSnackbar, hideSnackbar } = useSnackbar();

  const [components, setComponents] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [view, setView] = useState({ zoom: 0.4, panX: 0, panY: 0 });
  const svgRef = useRef(null);
  const panState = useRef(null);
  const dragState = useRef(null);

  const selected = useMemo(
    () => components.find((c) => c.clientId === selectedId) || null,
    [components, selectedId]
  );

  // 콘텐츠가 화면에 들어오도록 뷰 자동 맞춤 (초기 로드 + 전체 보기 버튼 공용)
  const fitToContent = useCallback((items) => {
    const list = items || components;
    if (!svgRef.current) return;
    const rect = svgRef.current.getBoundingClientRect();
    if (list.length === 0) {
      // 콘텐츠 없으면 월드 중앙으로
      const z = 0.4;
      setView({
        zoom: z,
        panX: rect.width / 2 - (WORLD_SIZE / 2) * z,
        panY: rect.height / 2 - (WORLD_SIZE / 2) * z,
      });
      return;
    }
    const xMin = Math.min(...list.map((c) => c.positionX));
    const yMin = Math.min(...list.map((c) => c.positionY));
    const xMax = Math.max(...list.map((c) => c.positionX + (c.width || 80)));
    const yMax = Math.max(...list.map((c) => c.positionY + (c.height || 40)));
    const bw = Math.max(1, xMax - xMin);
    const bh = Math.max(1, yMax - yMin);
    // 여백 20% 두고 영역이 화면에 다 들어오는 줌
    const z = clamp(
      Math.min((rect.width * 0.8) / bw, (rect.height * 0.8) / bh),
      MIN_ZOOM,
      MAX_ZOOM,
    );
    const cx = (xMin + xMax) / 2;
    const cy = (yMin + yMax) / 2;
    setView({
      zoom: z,
      panX: rect.width / 2 - cx * z,
      panY: rect.height / 2 - cy * z,
    });
  }, [components]);

  // Esc / F 키: 전체 보기
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'f' || e.key === 'F') {
        if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
        fitToContent();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [fitToContent]);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    getStoreSeats(storeId)
      .then((res) => {
        if (!mounted) return;
        const data = res?.data || res || {};
        const seats = (data.seats || []).map(mapSeatFromApi);
        const entrances = (data.entrances || []).map(mapEntranceFromApi);
        const counters = (data.counters || []).map(mapCounterFromApi);
        const all = [...seats, ...entrances, ...counters];
        setComponents(all);
        if (all.length > 0 && svgRef.current) {
          const xs = all.map((c) => c.positionX);
          const ys = all.map((c) => c.positionY);
          const xMin = Math.min(...xs);
          const yMin = Math.min(...ys);
          const xMax = Math.max(...all.map((c) => c.positionX + (c.width || 80)));
          const yMax = Math.max(...all.map((c) => c.positionY + (c.height || 40)));
          const cx = (xMin + xMax) / 2;
          const cy = (yMin + yMax) / 2;
          const rect = svgRef.current.getBoundingClientRect();
          const z = 0.5;
          setView({
            zoom: z,
            panX: rect.width / 2 - cx * z,
            panY: rect.height / 2 - cy * z,
          });
        }
      })
      .catch((err) => {
        showSnackbar(err.message || '좌석 배치도를 불러오지 못했습니다.', 'error');
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [storeId, showSnackbar]);

  // 화면 중앙 월드 좌표 계산
  const viewCenterWorld = useCallback(() => {
    const el = svgRef.current;
    if (!el) return { x: WORLD_SIZE / 2, y: WORLD_SIZE / 2 };
    const rect = el.getBoundingClientRect();
    const cx = rect.width / 2;
    const cy = rect.height / 2;
    return { x: (cx - view.panX) / view.zoom, y: (cy - view.panY) / view.zoom };
  }, [view]);

  const addComponent = (kind) => {
    const center = viewCenterWorld();
    let item;
    if (kind === 'SEAT') {
      item = {
        clientId: newClientId(),
        id: null,
        kind: 'SEAT',
        seatNumber: nextSeatNumber(components),
        seatType: 'DOUBLE',
        basePrice: 1000,
        positionX: Math.round(center.x - SIZE_TABLE.M.width / 2),
        positionY: Math.round(center.y - SIZE_TABLE.M.height / 2),
        maxOccupancy: 2,
        hasOutlet: true,
        width: SIZE_TABLE.M.width,
        height: SIZE_TABLE.M.height,
        size: 'M',
        isShared: true,
        isInUse: false,
      };
    } else if (kind === 'ENTRANCE') {
      item = {
        clientId: newClientId(),
        id: null,
        kind: 'ENTRANCE',
        label: '입구',
        positionX: Math.round(center.x - 40),
        positionY: Math.round(center.y - 20),
      };
    } else {
      item = {
        clientId: newClientId(),
        id: null,
        kind: 'COUNTER',
        label: '카운터',
        positionX: Math.round(center.x - SIZE_TABLE.M.width / 2),
        positionY: Math.round(center.y - SIZE_TABLE.M.height / 2),
        width: SIZE_TABLE.M.width,
        height: SIZE_TABLE.M.height,
        size: 'M',
      };
    }
    setComponents((prev) => [...prev, item]);
    setSelectedId(item.clientId);
  };

  const updateSelected = (patch) => {
    if (!selectedId) return;
    setComponents((prev) =>
      prev.map((c) => {
        if (c.clientId !== selectedId) return c;
        const next = { ...c, ...patch };
        if (c.kind === 'SEAT' && patch.maxOccupancy != null) {
          next.seatType = seatTypeFromCapacity(patch.maxOccupancy);
        }
        if ((c.kind === 'SEAT' || c.kind === 'COUNTER') && patch.size) {
          const dim = SIZE_TABLE[patch.size];
          if (dim) {
            next.width = dim.width;
            next.height = dim.height;
          }
        }
        return next;
      })
    );
  };

  const deleteSelected = () => {
    if (!selectedId) return;
    setComponents((prev) => prev.filter((c) => c.clientId !== selectedId));
    setSelectedId(null);
  };

  // SVG 좌표 변환
  const screenToWorld = (clientX, clientY) => {
    const rect = svgRef.current.getBoundingClientRect();
    const x = (clientX - rect.left - view.panX) / view.zoom;
    const y = (clientY - rect.top - view.panY) / view.zoom;
    return { x, y };
  };

  // 휠 줌 (stale closure 방지: 함수형 setState 사용)
  const onWheel = (e) => {
    e.preventDefault();
    if (!svgRef.current) return;
    const rect = svgRef.current.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;
    const delta = -e.deltaY * 0.0015;
    setView((v) => {
      const safeZoom = Number.isFinite(v.zoom) && v.zoom > 0 ? v.zoom : 0.4;
      const newZoom = clamp(safeZoom * (1 + delta), MIN_ZOOM, MAX_ZOOM);
      const ratio = newZoom / safeZoom;
      const newPanX = mx - (mx - v.panX) * ratio;
      const newPanY = my - (my - v.panY) * ratio;
      // NaN/Infinity 가드
      if (!Number.isFinite(newPanX) || !Number.isFinite(newPanY)) return v;
      return { zoom: newZoom, panX: newPanX, panY: newPanY };
    });
  };

  // 빈 공간 드래그 = 팬
  const onBgMouseDown = (e) => {
    if (e.button !== 0) return;
    panState.current = { startX: e.clientX, startY: e.clientY, origPanX: view.panX, origPanY: view.panY };
    setSelectedId(null);
  };

  // 좌석 드래그 시작
  const onItemMouseDown = (e, c) => {
    e.stopPropagation();
    setSelectedId(c.clientId);
    if (!svgRef.current) return;
    const start = screenToWorld(e.clientX, e.clientY);
    dragState.current = {
      clientId: c.clientId,
      offsetX: start.x - c.positionX,
      offsetY: start.y - c.positionY,
    };
  };

  const onMouseMove = (e) => {
    if (!svgRef.current) return;
    // race condition 방지: ref 값을 미리 캡쳐
    const drag = dragState.current;
    const pan = panState.current;
    if (drag) {
      const w = screenToWorld(e.clientX, e.clientY);
      const nx = Math.round(w.x - drag.offsetX);
      const ny = Math.round(w.y - drag.offsetY);
      if (!Number.isFinite(nx) || !Number.isFinite(ny)) return;
      setComponents((prev) =>
        prev.map((c) => (c.clientId === drag.clientId ? { ...c, positionX: nx, positionY: ny } : c))
      );
    } else if (pan) {
      const dx = e.clientX - pan.startX;
      const dy = e.clientY - pan.startY;
      const nx = pan.origPanX + dx;
      const ny = pan.origPanY + dy;
      if (!Number.isFinite(nx) || !Number.isFinite(ny)) return;
      setView((v) => ({ ...v, panX: nx, panY: ny }));
    }
  };

  const onMouseUp = () => {
    dragState.current = null;
    panState.current = null;
  };

  // 저장
  const handleSave = async () => {
    if (saving) return;
    // 검증
    const seats = components.filter((c) => c.kind === 'SEAT');
    const blank = seats.find((s) => !s.seatNumber || String(s.seatNumber).trim() === '');
    if (blank) {
      showSnackbar('좌석 번호가 비어있는 좌석이 있습니다.', 'error');
      return;
    }
    const seatNumbers = seats.map((s) => String(s.seatNumber).trim());
    const dup = seatNumbers.find((n, i) => seatNumbers.indexOf(n) !== i);
    if (dup) {
      showSnackbar(`좌석 번호 "${dup}" 가 중복됩니다.`, 'error');
      return;
    }
    const payload = {
      seats: seats.map((s) => ({
        id: s.id || undefined,
        seatNumber: String(s.seatNumber),
        seatType: s.seatType,
        basePrice: s.basePrice,
        positionX: Math.round(s.positionX),
        positionY: Math.round(s.positionY),
        maxOccupancy: s.maxOccupancy,
        hasOutlet: s.hasOutlet,
        width: s.width,
        height: s.height,
        size: s.size,
        isShared: s.isShared,
      })),
      entrances: components
        .filter((c) => c.kind === 'ENTRANCE')
        .map((e) => ({
          id: e.id || undefined,
          label: e.label,
          positionX: Math.round(e.positionX),
          positionY: Math.round(e.positionY),
        })),
      counters: components
        .filter((c) => c.kind === 'COUNTER')
        .map((c) => ({
          id: c.id || undefined,
          label: c.label,
          positionX: Math.round(c.positionX),
          positionY: Math.round(c.positionY),
          width: c.width,
          height: c.height,
          size: c.size,
        })),
      defaultBasePrice: 1000,
    };
    setSaving(true);
    try {
      const res = await updateStoreSeats(storeId, payload);
      const data = res?.data || res || {};
      const ns = (data.seats || []).map(mapSeatFromApi);
      const ne = (data.entrances || []).map(mapEntranceFromApi);
      const nc = (data.counters || []).map(mapCounterFromApi);
      setComponents([...ns, ...ne, ...nc]);
      setSelectedId(null);
      showSnackbar('저장 완료!', 'success');
    } catch (err) {
      showSnackbar(err.message || '저장에 실패했습니다.', 'error');
    } finally {
      setSaving(false);
    }
  };

  // 그리드 라인 좌표 (성능 위해 줌 따라 간격 조정 X — SVG 가 충분히 빠름)
  const gridLines = useMemo(() => {
    const lines = [];
    for (let i = 0; i <= WORLD_SIZE; i += GRID) {
      lines.push({ x1: i, y1: 0, x2: i, y2: WORLD_SIZE, key: `v${i}` });
      lines.push({ x1: 0, y1: i, x2: WORLD_SIZE, y2: i, key: `h${i}` });
    }
    return lines;
  }, []);

  const seatColor = (s) => {
    if (selectedId === s.clientId) return '#F85009';
    if (!s.isShared) return '#DFE0E1';
    if (s.isInUse) return '#FFD7C2';
    return '#FBFBFB';
  };

  return (
    <div className="admin-layout">
      <Header />
      <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 56px)' }}>
        {/* 상단 바 */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '12px 20px',
            borderBottom: '1px solid #e6e6e6',
            background: '#fff',
            gap: 12,
          }}
        >
          <button
            type="button"
            onClick={() => navigate(-1)}
            style={{ border: '1px solid #ccc', background: '#fff', padding: '6px 12px', borderRadius: 6, cursor: 'pointer' }}
          >
            ← 뒤로
          </button>
          <h2 style={{ margin: 0, fontSize: 16 }}>좌석 배치도 관리</h2>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <button type="button" onClick={() => fitToContent()} style={btnStyle()} title="F 키">전체 보기</button>
            <button type="button" onClick={() => addComponent('SEAT')} style={btnStyle()}>+ 좌석</button>
            <button type="button" onClick={() => addComponent('ENTRANCE')} style={btnStyle()}>+ 입구</button>
            <button type="button" onClick={() => addComponent('COUNTER')} style={btnStyle()}>+ 카운터</button>
            <button type="button" onClick={handleSave} disabled={saving} style={btnStyle(true)}>
              {saving ? '저장 중...' : '저장하기'}
            </button>
          </div>
        </div>

        {/* 본문: 좌측 캔버스 + 우측 패널 */}
        <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          <div style={{ flex: 1, position: 'relative', background: '#f3f3f3', overflow: 'hidden' }}>
            {loading && (
              <div style={overlayStyle()}>좌석 배치도 로딩 중...</div>
            )}
            <svg
              ref={svgRef}
              width="100%"
              height="100%"
              onWheel={onWheel}
              onMouseDown={onBgMouseDown}
              onMouseMove={onMouseMove}
              onMouseUp={onMouseUp}
              onMouseLeave={onMouseUp}
              style={{ cursor: panState.current ? 'grabbing' : 'default', userSelect: 'none' }}
            >
              <g transform={`translate(${Number.isFinite(view.panX) ? view.panX : 0}, ${Number.isFinite(view.panY) ? view.panY : 0}) scale(${Number.isFinite(view.zoom) && view.zoom > 0 ? view.zoom : 0.4})`}>
                {/* 월드 배경 */}
                <rect x={0} y={0} width={WORLD_SIZE} height={WORLD_SIZE} fill="#ffffff" stroke="#DFE0E1" strokeWidth={2} />
                {/* 그리드 */}
                {gridLines.map((l) => (
                  <line key={l.key} x1={l.x1} y1={l.y1} x2={l.x2} y2={l.y2} stroke="#EAEAEA" strokeWidth={1} />
                ))}
                {/* 컴포넌트들 */}
                {components.map((c) => {
                  if (c.kind === 'SEAT') {
                    return (
                      <g key={c.clientId} onMouseDown={(e) => onItemMouseDown(e, c)} style={{ cursor: 'move' }}>
                        <rect
                          x={c.positionX}
                          y={c.positionY}
                          width={c.width}
                          height={c.height}
                          fill={seatColor(c)}
                          stroke={selectedId === c.clientId ? 'none' : '#B0B0B0'}
                          strokeWidth={2}
                          rx={6}
                        />
                        <text
                          x={c.positionX + c.width / 2}
                          y={c.positionY + c.height / 2 - 6}
                          textAnchor="middle"
                          fontSize={20}
                          fontWeight="bold"
                          fill={selectedId === c.clientId ? '#fff' : '#343434'}
                        >
                          {c.seatNumber}번
                        </text>
                        <text
                          x={c.positionX + c.width / 2}
                          y={c.positionY + c.height / 2 + 16}
                          textAnchor="middle"
                          fontSize={14}
                          fill={selectedId === c.clientId ? '#fff' : '#666'}
                        >
                          {c.maxOccupancy}인석
                        </text>
                        {c.hasOutlet && (
                          <text
                            x={c.positionX + c.width / 2}
                            y={c.positionY + c.height - 8}
                            textAnchor="middle"
                            fontSize={12}
                            fill={selectedId === c.clientId ? '#fff' : '#888'}
                          >
                            🔌
                          </text>
                        )}
                      </g>
                    );
                  }
                  if (c.kind === 'ENTRANCE') {
                    return (
                      <g key={c.clientId} onMouseDown={(e) => onItemMouseDown(e, c)} style={{ cursor: 'move' }}>
                        <rect
                          x={c.positionX}
                          y={c.positionY}
                          width={80}
                          height={40}
                          fill={selectedId === c.clientId ? '#F85009' : '#343434'}
                          rx={6}
                        />
                        <text
                          x={c.positionX + 40}
                          y={c.positionY + 26}
                          textAnchor="middle"
                          fontSize={18}
                          fontWeight="bold"
                          fill="#FBFBFB"
                        >
                          입구
                        </text>
                      </g>
                    );
                  }
                  // COUNTER
                  return (
                    <g key={c.clientId} onMouseDown={(e) => onItemMouseDown(e, c)} style={{ cursor: 'move' }}>
                      <rect
                        x={c.positionX}
                        y={c.positionY}
                        width={c.width}
                        height={c.height}
                        fill={selectedId === c.clientId ? '#F85009' : '#343434'}
                        rx={6}
                      />
                      <text
                        x={c.positionX + c.width / 2}
                        y={c.positionY + c.height / 2 + 6}
                        textAnchor="middle"
                        fontSize={18}
                        fontWeight="bold"
                        fill="#FBFBFB"
                      >
                        카운터
                      </text>
                    </g>
                  );
                })}
              </g>
            </svg>
            {/* 줌 표시 */}
            <div style={{ position: 'absolute', bottom: 12, left: 12, background: '#fff', padding: '6px 10px', borderRadius: 6, boxShadow: '0 1px 4px rgba(0,0,0,0.1)', fontSize: 12 }}>
              줌 {Math.round((Number.isFinite(view.zoom) ? view.zoom : 0.4) * 100)}% · 휠로 줌, 빈 곳 드래그로 이동 · F 키 또는 "전체 보기" 로 복귀
            </div>
          </div>

          {/* 우측 속성 패널 */}
          <div style={{ width: 320, borderLeft: '1px solid #e6e6e6', background: '#fafafa', overflowY: 'auto', padding: 16 }}>
            {!selected && (
              <div style={{ color: '#888', fontSize: 14 }}>
                요소를 선택하면 속성을 편집할 수 있습니다.<br />
                상단의 + 버튼으로 추가, 캔버스에서 드래그로 이동.
              </div>
            )}
            {selected?.kind === 'SEAT' && (
              <SeatPanel
                seat={selected}
                onChange={updateSelected}
                onDelete={deleteSelected}
                storeId={storeId}
                showSnackbar={showSnackbar}
              />
            )}
            {selected?.kind === 'ENTRANCE' && (
              <EntrancePanel onDelete={deleteSelected} entrance={selected} />
            )}
            {selected?.kind === 'COUNTER' && (
              <CounterPanel counter={selected} onChange={updateSelected} onDelete={deleteSelected} />
            )}
          </div>
        </div>
      </div>
      <Snackbar message={snackbar.message} type={snackbar.type} isOpen={snackbar.isOpen} onClose={hideSnackbar} />
    </div>
  );
};

const btnStyle = (primary = false) => ({
  background: primary ? '#F85009' : '#fff',
  color: primary ? '#fff' : '#333',
  border: primary ? 'none' : '1px solid #ccc',
  padding: '8px 14px',
  borderRadius: 6,
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
});

const overlayStyle = () => ({
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'rgba(255,255,255,0.7)',
  fontSize: 16,
  color: '#666',
  zIndex: 5,
});

const labelStyle = { fontSize: 12, color: '#555', marginBottom: 4, display: 'block' };
const inputStyle = { width: '100%', padding: '6px 10px', border: '1px solid #ccc', borderRadius: 4, fontSize: 14, marginBottom: 12 };

const SeatPanel = ({ seat, onChange, onDelete, storeId, showSnackbar }) => {
  const [photos, setPhotos] = React.useState([]);
  const [photosLoading, setPhotosLoading] = React.useState(false);
  const [uploading, setUploading] = React.useState(false);
  const fileRef = React.useRef(null);

  // 좌석이 바뀌거나 신규(아직 id 없음) 일 때 처리
  React.useEffect(() => {
    let mounted = true;
    if (!seat.id) {
      setPhotos([]);
      return () => { mounted = false; };
    }
    setPhotosLoading(true);
    getSeatPhotos(storeId, seat.id)
      .then((res) => {
        if (mounted) setPhotos(res?.data?.photos || []);
      })
      .catch(() => {
        if (mounted) setPhotos([]);
      })
      .finally(() => {
        if (mounted) setPhotosLoading(false);
      });
    return () => { mounted = false; };
  }, [seat.id, storeId]);

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (files.length === 0) return;
    if (!seat.id) {
      showSnackbar('좌석을 먼저 저장한 뒤 사진을 업로드할 수 있어요.', 'error');
      return;
    }
    if (files.length > 2) {
      showSnackbar('좌석 사진은 최대 2장입니다.', 'error');
      return;
    }
    setUploading(true);
    try {
      const res = await uploadSeatPhotos(storeId, seat.id, files);
      setPhotos(res?.data?.photos || []);
      showSnackbar('사진 업로드 완료', 'success');
    } catch (err) {
      showSnackbar(err.message || '업로드에 실패했습니다.', 'error');
    } finally {
      setUploading(false);
    }
  };

  return (
  <div>
    <h3 style={{ margin: '0 0 12px', fontSize: 15 }}>좌석 #{seat.seatNumber}</h3>
    <label style={labelStyle}>좌석 번호</label>
    <input style={inputStyle} type="text" value={seat.seatNumber} onChange={(e) => onChange({ seatNumber: e.target.value })} />
    <label style={labelStyle}>인석 (수용 인원)</label>
    <input style={inputStyle} type="number" min="1" value={seat.maxOccupancy} onChange={(e) => onChange({ maxOccupancy: parseInt(e.target.value, 10) || 1 })} />
    <label style={labelStyle}>기본 가격 (원/시간)</label>
    <input style={inputStyle} type="number" min="0" step="100" value={seat.basePrice ?? ''} onChange={(e) => onChange({ basePrice: e.target.value === '' ? null : Number(e.target.value) })} />
    <label style={labelStyle}>크기</label>
    <div style={{ display: 'flex', gap: 4, marginBottom: 12 }}>
      {['S', 'M', 'L', 'XL'].map((s) => (
        <button key={s} type="button" onClick={() => onChange({ size: s })} style={{ flex: 1, padding: '6px 0', border: '1px solid #ccc', background: seat.size === s ? '#F85009' : '#fff', color: seat.size === s ? '#fff' : '#333', borderRadius: 4, cursor: 'pointer', fontWeight: 600 }}>{s}</button>
      ))}
    </div>
    <div style={{ display: 'flex', gap: 8 }}>
      <div style={{ flex: 1 }}>
        <label style={labelStyle}>너비</label>
        <input style={inputStyle} type="number" value={seat.width} onChange={(e) => onChange({ width: Number(e.target.value) })} />
      </div>
      <div style={{ flex: 1 }}>
        <label style={labelStyle}>높이</label>
        <input style={inputStyle} type="number" value={seat.height} onChange={(e) => onChange({ height: Number(e.target.value) })} />
      </div>
    </div>
    <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 6 }}>
      <input type="checkbox" checked={seat.hasOutlet} onChange={(e) => onChange({ hasOutlet: e.target.checked })} />
      <span>콘센트 있음</span>
    </label>
    <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 6 }}>
      <input type="checkbox" checked={seat.isShared} onChange={(e) => onChange({ isShared: e.target.checked })} />
      <span>공유 좌석 (활성)</span>
    </label>
    <div style={{ marginTop: 8, fontSize: 12, color: '#888' }}>
      위치: ({Math.round(seat.positionX)}, {Math.round(seat.positionY)}) · 타입: {seat.seatType}{seat.isInUse ? ' · 사용 중' : ''}
    </div>

    {/* 좌석 사진 슬롯 */}
    <div style={{ marginTop: 16, padding: 12, background: '#fff', border: '1px solid #e6e6e6', borderRadius: 6 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: '#333' }}>좌석 사진</span>
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading || !seat.id}
          title={!seat.id ? '먼저 저장 후 업로드 가능' : ''}
          style={{
            background: !seat.id ? '#ccc' : '#F85009',
            color: '#fff',
            border: 'none',
            padding: '4px 10px',
            borderRadius: 4,
            fontSize: 12,
            fontWeight: 600,
            cursor: !seat.id ? 'not-allowed' : 'pointer',
          }}
        >
          {uploading ? '업로드 중...' : '교체/업로드'}
        </button>
        <input ref={fileRef} type="file" accept="image/*" multiple onChange={handleFiles} style={{ display: 'none' }} />
      </div>
      {!seat.id && (
        <div style={{ fontSize: 11, color: '#888' }}>좌석을 먼저 저장하면 사진을 업로드할 수 있습니다.</div>
      )}
      {seat.id && photosLoading && <div style={{ fontSize: 12, color: '#888' }}>불러오는 중...</div>}
      {seat.id && !photosLoading && photos.length === 0 && (
        <div style={{ fontSize: 12, color: '#888' }}>등록된 사진이 없습니다. (최대 2장)</div>
      )}
      {photos.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
          {photos.map((p, i) => (
            <div key={`${p.url}-${i}`} style={{ position: 'relative', paddingBottom: '75%', background: '#f3f3f3', borderRadius: 4, overflow: 'hidden' }}>
              <img src={p.url} alt={`seat-${i}`} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
            </div>
          ))}
        </div>
      )}
      <div style={{ marginTop: 6, fontSize: 11, color: '#999' }}>
        업로드 시 기존 사진은 모두 교체됩니다.
      </div>
    </div>

    <button type="button" onClick={onDelete} style={{ marginTop: 16, width: '100%', padding: '10px', background: '#fff', color: '#F85009', border: '1px solid #F85009', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>삭제하기</button>
  </div>
  );
};

const EntrancePanel = ({ entrance, onDelete }) => (
  <div>
    <h3 style={{ margin: '0 0 12px', fontSize: 15 }}>입구</h3>
    <div style={{ fontSize: 13, color: '#666', marginBottom: 12 }}>
      위치: ({Math.round(entrance.positionX)}, {Math.round(entrance.positionY)})<br />
      드래그로 위치만 조정 가능합니다.
    </div>
    <button type="button" onClick={onDelete} style={{ width: '100%', padding: '10px', background: '#fff', color: '#F85009', border: '1px solid #F85009', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>삭제하기</button>
  </div>
);

const CounterPanel = ({ counter, onChange, onDelete }) => (
  <div>
    <h3 style={{ margin: '0 0 12px', fontSize: 15 }}>카운터</h3>
    <label style={labelStyle}>크기</label>
    <div style={{ display: 'flex', gap: 4, marginBottom: 12 }}>
      {['S', 'M', 'L', 'XL'].map((s) => (
        <button key={s} type="button" onClick={() => onChange({ size: s })} style={{ flex: 1, padding: '6px 0', border: '1px solid #ccc', background: counter.size === s ? '#F85009' : '#fff', color: counter.size === s ? '#fff' : '#333', borderRadius: 4, cursor: 'pointer', fontWeight: 600 }}>{s}</button>
      ))}
    </div>
    <div style={{ display: 'flex', gap: 8 }}>
      <div style={{ flex: 1 }}>
        <label style={labelStyle}>너비</label>
        <input style={inputStyle} type="number" value={counter.width} onChange={(e) => onChange({ width: Number(e.target.value) })} />
      </div>
      <div style={{ flex: 1 }}>
        <label style={labelStyle}>높이</label>
        <input style={inputStyle} type="number" value={counter.height} onChange={(e) => onChange({ height: Number(e.target.value) })} />
      </div>
    </div>
    <div style={{ fontSize: 12, color: '#888' }}>위치: ({Math.round(counter.positionX)}, {Math.round(counter.positionY)})</div>
    <button type="button" onClick={onDelete} style={{ marginTop: 16, width: '100%', padding: '10px', background: '#fff', color: '#F85009', border: '1px solid #F85009', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>삭제하기</button>
  </div>
);

export default StoreSeatLayout;
